import { ErrorBoundary } from 'react-error-boundary';
import { Route, Switch, useLocation } from 'wouter';
import ErrorPage from '../../ErrorPage';
import App, { UnauthedApp } from '../app';
import Dashboard from '../dashboard';
import HomePanel from '../dashboard/home/HomePanel';
import { ROUTES } from './consts';

import { ReactElement } from 'react';
import { RouterProvider } from 'react-aria';
import Admin from '../admin/Admin';
import { ApplicationStartPage } from '../auth/account-verification/ApplicationStartPage';
import { EmailVerificationPage } from '../auth/account-verification/EmailVerificationPage';
import { OneTimeLoginPage } from '../auth/account-verification/OneTimeLoginPage';
import { PasswordResetEmailVerificationPage } from '../auth/account-verification/PasswordResetEmailVerificationPage';
import { ChangePasswordPage } from '../auth/forgot-password/ChangePasswordPage';
import { ForgotPasswordPage } from '../auth/forgot-password/ForgotPasswordPage';
import { UpdatePasswordPage } from '../auth/forgot-password/UpdatePasswordPage';
import { HomeSignIn } from '../auth/sign-in/HomeSignIn';
import { HomeSignUp } from '../auth/sign-up/HomeSignUp';
import { InvitationSignUp } from '../auth/sign-up/invititation-sign-up/InvitationSignUp';
import { CommunityHomePage } from '../dashboard/community/community-home/CommunityHomePage';
import { CommunityWinsPage } from '../dashboard/community/community-wins/CommunityWinsPage';
import { EventsPage } from '../dashboard/community/events/EventsPage';
import { FundingOpportunitiesPage } from '../dashboard/community/funding-opportunities/FundingOpportunitiesPage';
import { JobPostingsPage } from '../dashboard/community/job_openings/JobOpeningsPage';
import MyProjectsPage from '../dashboard/my-projects';
import Healthcheck from '../Healthcheck';

// TODO: Configure with our custom routing options
// Note that this is RAC; may need similar for bare RA?
declare module 'react-aria-components' {
  interface RouterConfig {
    routerOptions: {
      optionOne: boolean;
      optionTwo: string;
    };
  }
}

const ReactAriaRouter = ({ children }: { children: ReactElement }) => {
  const [, setLocation] = useLocation();

  return (
    <RouterProvider
      // TODO: These navigate & useHref functions don't do anything that isn't default wouter behavior
      // yet. They were used to debug intra-app navigation and may need to accept options for supporting
      // analytics reporting
      navigate={(to) => {
        // Only use wouter for in-app spa navigations.
        // TODO: Does this break if the absolute url is to a different SPA page?
        if (!to.startsWith('/')) {
          window.location.href = to;
        } else {
          setLocation(to, {});
        }
      }}
      useHref={(href) => {
        return href;
      }}
    >
      {children}
    </RouterProvider>
  );
};

export const routerConfig = (
  <Route path={ROUTES.ROOT} nest>
    <ReactAriaRouter>
      <Switch>
        <Route path={ROUTES.AUTH} nest>
          <UnauthedApp>
            <Switch>
              <Route path={ROUTES.SIGN_UP}>
                <HomeSignUp />
              </Route>
              <Route path={ROUTES.EMAIL_VERIFICATION}>
                <EmailVerificationPage />
              </Route>
              <Route path={ROUTES.PASSWORD_RESET_EMAIL_VERIFICATION}>
                <PasswordResetEmailVerificationPage />
              </Route>
              <Route path={ROUTES.LOGIN}>
                <HomeSignIn />
              </Route>
              <Route path={ROUTES.WELCOME_LOGIN}>
                <OneTimeLoginPage />
              </Route>
              <Route path={ROUTES.FORGOT_PASSWORD}>
                <ForgotPasswordPage />
              </Route>
              <Route path={ROUTES.UPDATE_PASSWORD}>
                <UpdatePasswordPage />
              </Route>
              <Route path={ROUTES.APPLICATION_START}>
                <ApplicationStartPage />
              </Route>
              {/*temporary route for testing */}
              <Route path='/invitation-signup'>
                <InvitationSignUp />
              </Route>
            </Switch>
          </UnauthedApp>
        </Route>
        <Route path='/' nest>
          <App>
            {/* react-error-boundary is only supported in client contexts */}
            <ErrorBoundary FallbackComponent={ErrorPage}>
              <Switch>
                <Route path={ROUTES.DASHBOARD} nest>
                  <Dashboard>
                    <>
                      <Route path={ROUTES.MY_PROJECTS}>
                        <MyProjectsPage />
                      </Route>
                      <Route path={ROUTES.COMMUNITY} nest>
                        <Route path={ROUTES.JOB_OPENINGS}>
                          <JobPostingsPage />
                        </Route>
                        <Route path={ROUTES.EVENTS}>
                          <EventsPage />
                        </Route>
                        <Route path={ROUTES.FUNDING_OPPORTUNITIES}>
                          <FundingOpportunitiesPage />
                        </Route>
                        <Route path={ROUTES.COMMUNITY_WINS}>
                          <CommunityWinsPage />
                        </Route>
                        <Route path='/'>
                          <CommunityHomePage />
                        </Route>
                      </Route>
                      <Route path='/'>
                        <HomePanel />
                      </Route>
                    </>
                  </Dashboard>
                </Route>
                <Route path={ROUTES.PROFILE} nest>
                  <Route path={ROUTES.CHANGE_PASSWORD}>
                    <ChangePasswordPage />
                  </Route>
                </Route>
                <Route path={ROUTES.ADMIN} nest>
                  <Admin />
                </Route>
                <Route path={ROUTES.HEALTHCHECK} component={Healthcheck} />
                {/* Allows redirecting into the error page in additon to the default pass through below */}
                <Route path={ROUTES.ERROR} component={ErrorPage} />
                {/* Default error page shown for routes that don't match.
            TODO: Show a "couldn't be found" message instead of a generic error? */}
                <Route component={ErrorPage} />
              </Switch>
            </ErrorBoundary>
          </App>
        </Route>
      </Switch>
    </ReactAriaRouter>
  </Route>
);
